import { CSSProperties, useEffect, useState } from 'react';
import { FieldError } from 'react-hook-form';
import { InputActionMeta } from 'react-select';
import { useTheme } from 'styled-components';

import { Size } from 'coreOld/enums';
import { IOption } from 'coreOld/types';

import { createCustomStyles } from './asyncSelectStyle';
import AsyncSelectView from './AsyncSelectView';
import { formatLabel } from './utils';

interface IProps {
  key?: string;
  name: string;
  placeholder?: string;
  pageSize?: number;
  showDropdownIcon?: boolean;
  isMulti?: boolean;
  isClearable?: boolean;
  value?: string;
  defaultValue?: string;
  size?: Size;
  required?: boolean;
  fontSize?: number | string;
  startIcon?: JSX.Element;
  endIcon?: JSX.Element;
  onIconClick?: (event) => void;
  onSearchClick?: (searchText?: string) => void;
  disabled?: boolean;
  controlStyle?: CSSProperties;
  optionStyle?: CSSProperties;
  menuListStyle?: CSSProperties;
  iconStyle?: CSSProperties;
  requestPayload?: Record<string, any>;
  fontInherit?: boolean;
  hasBorder?: boolean;
  getOptions?;
  getValue?: (option) => string;
  getLabel?: (option) => string;
  onChange?;
  onInputChange?;
  control?;
  onBlur?;
  onKeyDown?;
  onFocus?: () => void;
  autoFocus?: boolean;
  error?: FieldError | string;
  showFieldError?: boolean;
  isCreatable?: boolean;
  createOption?: (label: string) => IOption;
  onCreateOption?: (inputValue: string) => void;
  formatCreateLabel?: (inputValue: string) => JSX.Element;
  formatOptionLabel?: (option, selectValue) => JSX.Element;
  initialInputText?: string;
}

const AsyncSelect = ({
  key,
  pageSize = 30,
  showDropdownIcon = false,
  isMulti = false,
  isClearable = true,
  required = false,
  disabled = false,
  getValue = (option) => option?.id,
  getLabel = (option) => option.name,
  value,
  defaultValue = '',
  onChange,
  onInputChange,
  name,
  placeholder = '',
  size = Size.LG,
  fontSize,
  startIcon,
  endIcon,
  onIconClick,
  onSearchClick,
  getOptions,
  control,
  requestPayload,
  controlStyle,
  optionStyle,
  menuListStyle,
  iconStyle,
  onBlur,
  onKeyDown,
  fontInherit = false,
  hasBorder = true,
  onFocus,
  autoFocus = false,
  error,
  showFieldError = false,
  isCreatable = false,
  createOption,
  onCreateOption,
  formatCreateLabel,
  formatOptionLabel,
  initialInputText = '',
}: IProps) => {
  const [searchText, setSearchText] = useState(initialInputText);

  useEffect(() => {
    setSearchText(initialInputText);
  }, [initialInputText]);

  const theme = useTheme();
  const customStyles = createCustomStyles(
    theme,
    size,
    controlStyle,
    optionStyle,
    menuListStyle,
    fontSize,
    Boolean(startIcon),
    Boolean(endIcon),
    fontInherit,
    hasBorder,
    Boolean(error)
  );

  const handleOnIconClick = (event, search: string) => {
    if (onIconClick) {
      onIconClick(event);
    }

    if (onSearchClick) {
      onSearchClick(search);
    }
  };

  const handleInputChange = (inputValue, { action }: InputActionMeta) => {
    if (onInputChange) {
      onInputChange(inputValue, action);
    }

    if (action !== 'input-blur' && action !== 'menu-close') {
      setSearchText(inputValue);
    }
  };

  const loadOptions = async (search, prevOptions, additional) => {
    const { records, count } = await getOptions({
      pageSize,
      ...requestPayload,
      page: additional.page,
      q: search,
    });

    const loadedOptionsCount = prevOptions.length + records.length;

    return {
      options: records,
      hasMore: loadedOptionsCount < count,
      additional: {
        page: additional.page + 1,
      },
    };
  };

  const mapDataToFilters = (options, onChange) => {
    const data = options.map((item) => getValue(item) || item);

    onChange(data);
  };

  const handleGetLabel = (option) => {
    if (isCreatable && option.__isNew__) {
      return option.label;
    }

    return getLabel(option);
  };

  const handleFormatOptionLabel = (option, { selectValue }) => {
    let label = handleGetLabel(option);

    if (formatOptionLabel) {
      label = formatOptionLabel(option, selectValue);
    }

    return formatLabel(label, searchText, option.__isNew__);
  };

  return (
    <AsyncSelectView
      key={key}
      name={name}
      loadOptions={loadOptions}
      placeholder={placeholder}
      pageSize={pageSize}
      getValue={getValue}
      getLabel={handleGetLabel}
      formatOptionLabel={handleFormatOptionLabel}
      value={value}
      searchText={searchText}
      onChange={onChange}
      onInputChange={handleInputChange}
      mapDataToFilters={mapDataToFilters}
      customStyles={customStyles}
      iconStyle={iconStyle}
      showDropdownIcon={showDropdownIcon}
      isMulti={isMulti}
      isClearable={isClearable}
      required={required}
      startIcon={startIcon}
      endIcon={endIcon}
      onIconClick={handleOnIconClick}
      disabled={disabled}
      control={control}
      defaultValue={defaultValue}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      autoFocus={autoFocus}
      isCreatable={isCreatable}
      createOption={createOption}
      onCreateOption={onCreateOption}
      formatCreateLabel={formatCreateLabel}
      error={error}
      showFieldError={showFieldError}
    />
  );
};

export default AsyncSelect;
